import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { DockMode } from "@/types/assistant.types";

// Estado do dock persistido (key opus-assistant-dock): dock reaberto após F5 retoma
// lado/tamanho e a MESMA conversa (parte da retomada completa). UI-state puro (nada de dado de servidor).

const DEFAULT_WIDTH = 420; // px (lado direito)
const DEFAULT_HEIGHT = 360; // px (inferior)

interface AssistantDockState {
  open: boolean;
  mode: DockMode;
  size: number; // largura (right) ou altura (bottom), em px
  activeConversationId: string | null;
  setOpen: (open: boolean) => void;
  toggleOpen: () => void;
  setMode: (mode: DockMode) => void;
  setSize: (size: number) => void;
  setActiveConversation: (id: string | null) => void;
}

export const DOCK_DEFAULT_SIZE = { right: DEFAULT_WIDTH, bottom: DEFAULT_HEIGHT } as const;

// Migração v1→v2 (dock flutuante): o campo `side` ("right"|"bottom") virou `mode` (+ "floating").
export function migrateDockState(persisted: unknown, version: number): unknown {
  if (version < 2 && persisted && typeof persisted === "object" && "side" in persisted) {
    const { side, ...rest } = persisted as Record<string, unknown>;
    return { ...rest, mode: side };
  }
  return persisted;
}

export const useAssistantDock = create<AssistantDockState>()(
  persist(
    (set) => ({
      open: false,
      mode: "floating",
      size: DEFAULT_WIDTH,
      activeConversationId: null,
      setOpen: (open) => set({ open }),
      toggleOpen: () => set((s) => ({ open: !s.open })),
      // Floating tem tamanho fixo (CSS) ⇒ não mexe no size; ancorados voltam ao default do modo.
      setMode: (mode) =>
        set(mode === "floating" ? { mode } : { mode, size: DOCK_DEFAULT_SIZE[mode] }),
      setSize: (size) => set({ size }),
      setActiveConversation: (activeConversationId) => set({ activeConversationId }),
    }),
    {
      name: "opus-assistant-dock",
      version: 2,
      migrate: (persisted, version) => migrateDockState(persisted, version) as AssistantDockState,
      partialize: (s) => ({
        open: s.open,
        mode: s.mode,
        size: s.size,
        activeConversationId: s.activeConversationId,
      }),
    }
  )
);
